/* eslint max-lines-per-function: ["error", 248]*/
import type { TFunction } from 'i18next';

import { validateAgainst } from '../validation/validateAgainst';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import {
  CreateUpdateClientDto,
  CreateUpdateClientDtoFromJSON,
  DefaultApi,
  GetClientDto,
  GetClientExtendedDto,
  GetLastEditorDto,
  GusClientDto,
} from '../autogenerated/pokApiClient';
import { ClientSchema } from '../validation/schemas';
import { clientForGrid } from '../../../pok/components/Clients/ClientSearch';
import {
  Option,
  defaultSelectorLimit,
} from '../../../pok/components/Selects/Selector';
import { clientWithInvoiceNumbersForGrid } from '../../../pok/components/Clients/ClientWithoutSymphonySearch';
import {
  clientBasicForGrid,
  clientWithContactsForGrid,
} from '../../../pok/components/Clients/ClientWithContactsSearch';
import { FilterInvoiceTypeEnum } from '../validation/enums';
import { TKeys } from '../../hooks/useTranslation';

export type Client = GetClientDto | CreateUpdateClientDto;

export const newClient = () => {
  return CreateUpdateClientDtoFromJSON({
    country: 'Polska',
  });
};

export interface ClientContextInterface {
  getById: (id: string) => Promise<GetClientDto>;
  getByIdFormattedAddress: (id: string) => Promise<GetClientExtendedDto>;
  getAllByPortion: (
    t: TFunction<'clientLng', 'clientLng'>,
    tk: TKeys<'clientLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllCount: (filterText?: string, filterColumns?: string) => Promise<number>;
  getAllWithSymphonyByPortion: (
    t: TFunction<'clientLng', 'clientLng'>,
    tk: TKeys<'clientLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllWithSymphonyCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getAllWithoutSymphonyByPortion: (
    invoiceType: FilterInvoiceTypeEnum,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllWithoutSymphonyCount: (
    invoiceType: FilterInvoiceTypeEnum,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  create: (dto: CreateUpdateClientDto) => Promise<GetClientDto>;
  update: (id: string, dto: CreateUpdateClientDto) => Promise<void>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  getByNip: (nip: string) => Promise<GusClientDto>;
  getOptionsByNipPeselOrName: (
    t: TFunction<'clientLng', 'clientLng'>,
    tk: TKeys<'clientLng'>,
    nipPeselOrName: string,
  ) => Promise<Option[]>;
  checkActivePurchaserPositions: (id: string) => Promise<boolean>;
  getAllWithFVSEmailByPortion: (
    t: TFunction<'clientLng', 'clientLng'>,
    tk: TKeys<'clientLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllWithFVSEmailCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getWithContactsByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    lang?: string,
  ) => Promise<GridRecord[]>;
  getWithContactsCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getWithoutContactTypeByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    contactTypeId?: string,
  ) => Promise<GridRecord[]>;
  getWithoutContactTypeCount: (
    filterText?: string,
    filterColumns?: string,
    contactTypeId?: string,
  ) => Promise<number>;
}

export const ClientContext = (api: DefaultApi) => {
  return {
    getById: (id: string) => api.clientControllerGet(id),

    getByIdFormattedAddress: (id: string) =>
      api.clientControllerFormattedAddress(id),

    getAllByPortion: async (
      t: TFunction<'clientLng', 'clientLng'>,
      tk: TKeys<'clientLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      additionalId?: string,
    ) => {
      let data = await api.clientControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      if (additionalId) {
        data = data.map(o => {
          return { ...o, headId: additionalId };
        });
      }

      return data.map(item => clientForGrid(t, tk, item));
    },

    getAllCount: (filterText?: string, filterColumns?: string) =>
      api.clientControllerGetAllCount(filterText || '', filterColumns),

    getAllWithSymphonyByPortion: async (
      t: TFunction<'clientLng', 'clientLng'>,
      tk: TKeys<'clientLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      additionalId?: string,
    ) => {
      let data = await api.clientControllerGetAllWithSymphonyByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      if (additionalId) {
        data = data.map(o => {
          return { ...o, headId: additionalId };
        });
      }

      return data.map(item => clientForGrid(t, tk, item));
    },

    getAllWithSymphonyCount: (filterText?: string, filterColumns?: string) =>
      api.clientControllerGetAllWithSymphonyCount(
        filterText || '',
        filterColumns,
      ),

    getAllWithoutSymphonyByPortion: async (
      invoiceType: FilterInvoiceTypeEnum,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      additionalId?: string,
    ) => {
      let data = await api.clientControllerGetAllWithoutSymphonyByPortion(
        invoiceType,
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      if (additionalId) {
        data = data.map(o => {
          return { ...o, headId: additionalId };
        });
      }

      return data.map(clientWithInvoiceNumbersForGrid);
    },

    getAllWithoutSymphonyCount: (
      invoiceType: FilterInvoiceTypeEnum,
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.clientControllerGetAllWithoutSymphonyCount(
        invoiceType,
        filterText || '',
        filterColumns,
      ),

    create: (client: CreateUpdateClientDto) =>
      api.clientControllerCreate(client),

    update: (id: string, dto: CreateUpdateClientDto) =>
      api.clientControllerUpdate(id, dto),

    getLastEditor: (id: string) => api.clientControllerGetLastEditor(id),

    getByNip: (nip: string) => api.gusApiControllerGetByNip(nip),

    getOptionsByNipPeselOrName: async (
      t: TFunction<'clientLng', 'clientLng'>,
      tk: TKeys<'clientLng'>,
      text: string,
    ) => {
      const data = await api.clientControllerGetByNipPeselOrName(
        text,
        defaultSelectorLimit,
      );

      return Array.from(data, client => {
        return {
          value: client.id,
          label:
            client.name +
            ' ' +
            (client.nip
              ? `(${t(tk.client.nip)}: ` + client.nip + ') '
              : client.pesel
                ? `(${t(tk.client.pesel)}: ` + client.pesel + ') '
                : ''),
        };
      });
    },
    checkActivePurchaserPositions: async (id: string) => {
      const result =
        await api.clientControllerCheckActivePurchaserPositions(id);
      return result.toString() === 'true';
    },
    getAllWithFVSEmailByPortion: async (
      t: TFunction<'clientLng', 'clientLng'>,
      tk: TKeys<'clientLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      additionalId?: string,
    ) => {
      let data = await api.clientControllerGetAllWithFVSEmailByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      if (additionalId) {
        data = data.map(o => {
          return { ...o, headId: additionalId };
        });
      }

      return data.map(item => clientForGrid(t, tk, item));
    },

    getAllWithFVSEmailCount: (filterText?: string, filterColumns?: string) =>
      api.clientControllerGetAllWithFVSEmailCount(
        filterText || '',
        filterColumns,
      ),

    getWithContactsByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      lang?: string,
    ) => {
      const data = await api.clientControllerGetWithContactsByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      return data.map(item => clientWithContactsForGrid(item, lang || 'PL'));
    },

    getWithContactsCount: (filterText?: string, filterColumns?: string) =>
      api.clientControllerGetWithContactsCount(filterText || '', filterColumns),

    getWithoutContactTypeByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      contactTypeId?: string,
    ) => {
      const data = await api.clientControllerGetWithoutContactTypeByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        contactTypeId || '',
        filterColumns,
      );
      return data.map(clientBasicForGrid);
    },

    getWithoutContactTypeCount: (
      filterText?: string,
      filterColumns?: string,
      contactTypeId?: string,
    ) =>
      api.clientControllerGetWithoutContactTypeCount(
        filterText || '',
        contactTypeId || '',
        filterColumns,
      ),
  };
};

export const validate = (client: CreateUpdateClientDto) =>
  validateAgainst(ClientSchema, client);

// export const partial = (property: keyof Client, value: string) => {
//   const obj: Partial<Client> = {};
//   obj[property] = value;
//   return obj;
// };

export const convert = (client?: GetClientDto) => {
  if (!client) {
    return newClient();
  }
  const converted = CreateUpdateClientDtoFromJSON(client);
  converted.countryId = client.country.id;
  converted.countryIsMain = client.country.isMain;
  return converted;
};
